/**
 * Public privacy policy page (no auth). Linked from the Google OAuth consent
 * screen. Content reflects what the app actually collects and does.
 */
export function Privacy() {
  return (
    <div className="container" style={{ maxWidth: 760, padding: "2rem 1rem" }}>
      <h1>Privacy Policy</h1>
      <p className="muted">Last updated: October 2026</p>

      <p>
        Wrestling Meet Tracker ("the app", "we") helps wrestling parents and
        coaches discover meets, RSVP, coordinate carpools, invite others, add
        meets to their calendar, and track match history. This policy explains
        what we collect and how we use it.
      </p>

      <h2>Information we collect</h2>
      <ul>
        <li>
          <strong>Account info</strong> — your name and email address, obtained
          when you sign up (email/password) or sign in with Google.
        </li>
        <li>
          <strong>Profile details you provide</strong> — optional phone number
          and home address (used only to calculate distance to meets).
        </li>
        <li>
          <strong>App activity</strong> — teams you join, meets you create or
          RSVP to, invites, carpool entries, team messages, private notes, and
          wrestler match history you add or import.
        </li>
        <li>
          <strong>Google data (only if you link Google Calendar)</strong> — we
          request the <code>calendar.events</code> scope solely to create or
          update calendar events for meets you choose to add. We do not read
          your existing calendar and we store only a reference to events we
          create on your behalf.
        </li>
      </ul>

      <h2>How we use information</h2>
      <ul>
        <li>To operate core features (meets, RSVPs, invites, carpools, threads).</li>
        <li>To geocode addresses (via Google Maps) for distance sorting.</li>
        <li>
          To send notifications you opt into (meet reminders, invites,
          cancellations) by email or SMS.
        </li>
        <li>To add meets to your Google Calendar when you request it.</li>
      </ul>

      <h2>Sharing &amp; visibility</h2>
      <ul>
        <li>
          Team-scoped data (attendee lists, carpools, team threads) is visible
          to members of that team. Private notes are visible only to you.
        </li>
        <li>
          We do not sell your personal information. We share data only with the
          service providers that run the app (AWS), send messages (Twilio
          SendGrid / Twilio), and provide Google Calendar/Maps.
        </li>
        <li>
          Children's wrestlers: match history you link is kept private to your
          household/team and is never made public.
        </li>
      </ul>

      <h2>Google API data</h2>
      <p>
        Our use of information received from Google APIs adheres to the{" "}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. Google Calendar access is used
        only to create/update events for meets you add, and you can unlink
        Google at any time from your Profile.
      </p>

      <h2>Data retention &amp; deletion</h2>
      <p>
        You can delete meets, notes, carpool entries, and unlink Google from
        within the app. To delete your account and associated data, contact us
        at the email below.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about this policy? Email{" "}
        <a href="mailto:seamus.owens@gmail.com">seamus.owens@gmail.com</a>.
      </p>

      <p style={{ marginTop: "1.5rem" }}>
        <a href="/">← Back to app</a> · <a href="/terms">Terms of Service</a>
      </p>
    </div>
  );
}
